import { useEffect, useRef } from 'react';
import { useAuth } from "../context/AuthContext";

const SessionTimeoutHandler = () => {
  const { logout } = useAuth();
  const timerRef = useRef(null);
  const sessionTimeout = 30 * 60 * 1000; // 30 minutes in milliseconds
//   const sessionTimeout = 1 * 60 * 1000; // 01 minutes in milliseconds
  const hasLoggedOutRef = useRef(false);
  const isRefreshingRef = useRef(false);

  const resetTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      logout();
    }, sessionTimeout);
  };

  // Detect if page is being refreshed - check on component mount
  useEffect(() => {
    // Check if current page load was a refresh
    try {
      const navigation = performance.getEntriesByType('navigation')[0];
      if (navigation && navigation.type === 'reload') {
        // Current page was loaded via refresh - clear any stale flags
        sessionStorage.removeItem('isPageRefresh');
      }
    } catch (e) {
      // Fallback: check legacy navigation API
      if (performance.navigation && performance.navigation.type === 1) {
        sessionStorage.removeItem('isPageRefresh');
      }
    }
  }, []);

  // Detect refresh attempt before page unloads
  const handleBeforeUnload = (event) => {
    // Check if user is trying to refresh (F5, Ctrl+R, etc.)
    // We can't perfectly detect this, but we can try
    // Set a flag that will be checked in pagehide
    // Note: This is not 100% reliable, but helps in most cases
    
    // Check if there's a navigation happening (refresh)
    try {
      // If the page is being refreshed, the navigation type might indicate it
      // But we can't check this reliably in beforeunload
      // Instead, we'll rely on pagehide's persisted property
    } catch (e) {
      // Ignore errors
    }
  };

  // DISABLED: Handle logout when tab/browser is closed
  // This was causing logout time to be set on page refresh
  // Logout time will now only be updated on explicit logout or session timeout
  const handlePageUnload = (event) => {
    // Disabled - don't update logout time on page unload
    // This prevents false positives on page refresh
    // Logout time will remain "Active" until explicit logout or session timeout
    return;
  };


  // Handle visibility change (tab becomes hidden)
  // We track this but don't logout immediately - only on actual page unload
  const handleVisibilityChange = () => {
    if (!document.hidden) {
      // Tab became visible again - reset the logout flag
      hasLoggedOutRef.current = false;
    }
  };

  useEffect(() => {
    // List of events to consider as user activity
    const events = ['mousemove', 'keydown', 'click', 'scroll'];
    events.forEach((event) => window.addEventListener(event, resetTimer));
    resetTimer();

    // Add beforeunload to detect refresh before pagehide fires
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Add pagehide event listener - check persisted property to avoid refresh
    // persisted = true means page is being cached (refresh), persisted = false means actual unload (close)
    window.addEventListener('pagehide', handlePageUnload);
    
    // Add visibilitychange event listener to track tab visibility
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach((event) => window.removeEventListener(event, resetTimer));
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default SessionTimeoutHandler;
